import { Fragment } from 'react';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';

// Hooks
import usePagination from '../../../hooks/usePagination';

// Styles
import { table_container_base } from '../../../static/styles';

const statusColors = {
  'Active': '#4caf50',
  'In Progress': '#ff9800',
  'Completed': '#2196f3',
  'Cancelled': '#f44336',
};

const MobileArchiveCard = ({ appointment }) => (
  <Card variant="outlined" sx={{ mb: 2, borderLeft: `4px solid ${statusColors[appointment.status] || '#999'}` }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Chip label={appointment.status} size="small" sx={{ bgcolor: `${statusColors[appointment.status]}20`, color: statusColors[appointment.status] }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{moment(appointment.start_appt).format('MM/DD/YY')}</Typography>
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{appointment.type || 'No type'}</Typography>
      {appointment.tech && <Typography variant="body2" color="text.secondary">Tech: {appointment.tech}</Typography>}
      {appointment.source && <Typography variant="body2" color="text.secondary">Source: {appointment.source}</Typography>}
      {appointment.client_name && <Typography variant="body2" color="text.secondary">Client: {appointment.client_name}</Typography>}
      {appointment.notes && (
        <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.8rem' }}>
          {appointment.notes}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const PropertyArchiveTable = ({ appointments }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  return (
    <Fragment>
      {!isMobile && (
        <TableContainer component={Paper} sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="10%">Type</TableCell>
                <TableCell width="10%">Date</TableCell>
                <TableCell width="10%">Status</TableCell>
                <TableCell width="10%">Tech</TableCell>
                <TableCell width="10%">Source</TableCell>
                <TableCell width="40%">Notes</TableCell>
                <TableCell width="10%">Client</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.length ? (
                appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell>{appointment.type}</TableCell>
                    <TableCell>{moment(appointment.start_appt).format('MM/DD/YYYY')}</TableCell>
                    <TableCell>{appointment.status}</TableCell>
                    <TableCell>{appointment.tech}</TableCell>
                    <TableCell>{appointment.source}</TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'wrap' }}>{appointment.notes}</TableCell>
                    <TableCell>{appointment.client_name}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center' }}><strong>No Data</strong></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isMobile && (
        <Box>
          {appointments.length ? (
            appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
              <MobileArchiveCard key={appointment.id} appointment={appointment} />
            ))
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 3 }}>No archive records</Typography>
          )}
        </Box>
      )}

      {!isMobile && (
        <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={appointments.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Per page" />
      )}

      {isMobile && appointments.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page - 1)} disabled={page === 0}>Prev</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>{page + 1}/{Math.ceil(appointments.length / rowsPerPage)}</Typography>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page + 1)} disabled={(page + 1) * rowsPerPage >= appointments.length}>Next</Button>
        </Box>
      )}
    </Fragment>
  );
};

export default PropertyArchiveTable;
